import { create } from 'zustand';
import ServiceDctf from '@/services/ServiceDctf';
import { notify } from '@/stores/useToast';
import { mensagemErro } from '@/utils/apiError';

type Any = any;

const SORT_ALIASES: Record<string, string> = {
  empresaRazaoSocial: 'empresaRazaoSocial',
  periodoApur: 'periodoApur',
  debitoApurado: 'debitoApurado',
  deducoes: 'deducoes',
  outrosCreditos: 'outrosCreditos',
  pagamentos: 'pagamentos',
  saldoAPagar: 'saldoAPagar',
};

const initialPageSize = 50;

const normalizar = (d: Any) => ({
  ...d,
  empresaRazaoSocial: d.empresaRazaoSocial ?? d.razaoSocial ?? '',
  empresaCnpj: d.empresaCnpj ?? d.cnpj ?? '',
  periodoApur:
    d.periodoApur ??
    (d.mesApuracao != null
      ? `${String(d.mesApuracao).padStart(2, '0')}/${d.anoApuracao}`
      : String(d.anoApuracao ?? '')),
  debitoApurado: d.debitoApurado ?? d.debitoTotal ?? 0,
  deducoes: d.deducoes ?? 0,
  outrosCreditos: d.outrosCreditos ?? 0,
  pagamentos: d.pagamentos ?? 0,
  saldoAPagar: d.saldoAPagar ?? d.saldoAPagarTotal ?? 0,
});

interface State {
  cnpjs: string[];
  periodos: string[];
  situacao: boolean | undefined;
  possuiDcomp: boolean | undefined;
  periodosDisponiveis: string[];
  lista: Any[];
  total: number;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  carregando: boolean;
  carregandoPeriodos: boolean;

  inicializar: (cnpjs: string[]) => Promise<void>;
  aplicarFiltros: (
    cnpjs: string[],
    periodos: string[],
    situacao?: boolean,
    possuiDcomp?: boolean,
  ) => Promise<void>;
  carregarPeriodos: (cnpjs: string[]) => Promise<void>;
  limparLista: () => void;
  carregar: () => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setSize: (size: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
}

let listaReq = 0;
let periodosReq = 0;

export const useDctfStore = create<State>((set, get) => ({
  cnpjs: [],
  periodos: [],
  situacao: undefined,
  possuiDcomp: undefined,
  periodosDisponiveis: [],
  lista: [],
  total: 0,
  page: 0,
  size: initialPageSize,
  sortBy: 'periodoApur',
  sortOrder: 'desc',
  carregando: false,
  carregandoPeriodos: false,

  async inicializar(cnpjs) {
    const distinctCnpjs = [...new Set(cnpjs)];
    set({
      cnpjs: distinctCnpjs,
      periodos: [],
      situacao: undefined,
      possuiDcomp: undefined,
      lista: [],
      total: 0,
      page: 0,
      size: initialPageSize,
      sortBy: 'periodoApur',
      sortOrder: 'desc',
      carregando: distinctCnpjs.length > 0,
    });
    await get().carregarPeriodos(distinctCnpjs);
    await get().carregar();
  },

  async aplicarFiltros(cnpjs, periodos, situacao, possuiDcomp) {
    const distinctCnpjs = [...new Set(cnpjs)];
    set({
      cnpjs: distinctCnpjs,
      periodos: [...new Set(periodos)],
      situacao,
      possuiDcomp,
      page: 0,
      carregando: distinctCnpjs.length > 0,
    });
    await get().carregar();
  },

  async carregarPeriodos(cnpjs) {
    const request = ++periodosReq;
    const distinctCnpjs = [...new Set(cnpjs)];
    if (distinctCnpjs.length === 0) {
      set({ periodosDisponiveis: [], carregandoPeriodos: false });
      return;
    }
    set({ carregandoPeriodos: true, periodosDisponiveis: [] });
    try {
      const { data } = await ServiceDctf.buscarPeriodosApuracao(distinctCnpjs);
      if (request === periodosReq) {
        set({ periodosDisponiveis: Array.isArray(data) ? data : [] });
      }
    } catch (err) {
      if (request === periodosReq) {
        set({ periodosDisponiveis: [] });
        notify.error(mensagemErro(err, 'Erro ao carregar os períodos de apuração.'));
      }
    } finally {
      if (request === periodosReq) set({ carregandoPeriodos: false });
    }
  },

  limparLista() {
    listaReq += 1;
    periodosReq += 1;
    set({
      cnpjs: [],
      periodos: [],
      situacao: undefined,
      possuiDcomp: undefined,
      periodosDisponiveis: [],
      lista: [],
      total: 0,
      page: 0,
      carregando: false,
      carregandoPeriodos: false,
    });
  },

  async carregar() {
    const { cnpjs, periodos, situacao, possuiDcomp, page, size, sortBy, sortOrder } = get();
    const request = ++listaReq;
    if (cnpjs.length === 0) {
      set({ lista: [], total: 0, carregando: false });
      return;
    }
    set({ carregando: true });
    try {
      const alias = SORT_ALIASES[sortBy];
      const sort = alias ? `${alias},${sortOrder}` : undefined;
      const { data } = await ServiceDctf.search({
        cnpjs,
        periodos,
        page,
        size,
        ...(sort ? { sort } : {}),
        ...(situacao !== undefined ? { situacao } : {}),
        ...(possuiDcomp !== undefined ? { possuiDcomp } : {}),
      });
      if (request === listaReq) {
        set({ lista: (data?.items ?? []).map(normalizar), total: data?.total ?? 0 });
      }
    } catch {
      if (request === listaReq) {
        set({ lista: [], total: 0 });
        notify.error('Erro ao carregar as declarações DCTF.');
      }
    } finally {
      if (request === listaReq) set({ carregando: false });
    }
  },

  async setPage(page) {
    set({ page });
    await get().carregar();
  },

  async setSize(size) {
    set({ size, page: 0 });
    await get().carregar();
  },

  async setSort(by, order) {
    set({ sortBy: by, sortOrder: order, page: 0 });
    await get().carregar();
  },
}));
